import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/config/api';
import { useAuthContext } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, FileText, Mail, LogIn, AlertTriangle } from 'lucide-react';
import PostSigningModal from './PostSigningModal';
import SigningView from './SigningView';
import i18n from '@/i18n';

type GateStep = 'loading' | 'error' | 'already_actioned' | 'session_mismatch' | 'has_account' | 'otp_send' | 'otp_verify' | 'signing';

interface ResolveData {
  signatory_id: number;
  masked_email: string;
  has_account: boolean;
  status: string;
  document_title: string;
  requestor_name: string;
  locale?: string;
}

export default function SigningGate() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('signinggate');
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading, logout } = useAuthContext();

  const [step, setStep] = useState<GateStep>('loading');
  const [resolveData, setResolveData] = useState<ResolveData | null>(null);
  const [error, setError] = useState('');

  // OTP state
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // Post-signing modal
  const [showPostSigningModal, setShowPostSigningModal] = useState(false);

  // Verified OTP token for signing view
  const [verifiedOtpToken, setVerifiedOtpToken] = useState('');

  const apiFetch = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    const authToken = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
        ...((options.headers as Record<string, string>) || {}),
      },
    });
    return response;
  }, []);

  // Resolve the token on mount
  useEffect(() => {
    if (!token || authLoading) return;

    const resolve = async () => {
      try {
        const res = await apiFetch(`/sign/${token}/resolve`);
        if (!res.ok) {
          setStep('error');
          return;
        }

        const data: ResolveData = await res.json();
        setResolveData(data);

        // Set the UI language to the recipient's preferred locale
        if (data.locale && ['en', 'fr'].includes(data.locale)) {
          i18n.changeLanguage(data.locale);
        }

        // If already signed/declined
        if (data.status !== 'pending' && data.status !== 'waiting') {
          setStep('already_actioned');
          return;
        }

        // If user is authenticated, check if they are the signer
        if (isAuthenticated && user) {
          if (user.id === data.signatory_id) {
            // Logged-in user IS the intended signer — go directly to signing
            setVerifiedOtpToken('');
            setStep('signing');
          } else {
            // Logged-in user is NOT the signer — session mismatch
            setStep('session_mismatch');
          }
          return;
        }

        // Not authenticated
        if (data.has_account) {
          setStep('has_account');
        } else {
          setStep('otp_send');
        }
      } catch {
        setStep('error');
      }
    };

    resolve();
  }, [token, authLoading, isAuthenticated, user, apiFetch]);

  const handleSendOtp = async () => {
    if (!token) return;
    setIsSendingOtp(true);
    setError('');

    try {
      const res = await apiFetch(`/sign/${token}/send-otp`, { method: 'POST' });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || t('error_generic'));
        return;
      }

      setOtpToken(data.otp_token);
      setStep('otp_verify');
    } catch {
      setError(t('error_generic'));
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!token || !otpToken) return;
    setIsVerifyingOtp(true);
    setError('');

    try {
      const res = await apiFetch(`/sign/${token}/verify-otp`, {
        method: 'POST',
        body: JSON.stringify({ otp_token: otpToken, code: otpCode }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.message || t('code_invalid'));
        return;
      }

      setVerifiedOtpToken(data.otp_token);
      setStep('signing');
    } catch {
      setError(t('error_generic'));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpCode('');
    await handleSendOtp();
  };

  const handleSignInRedirect = () => {
    // Store the signing token so we can redirect back after login
    sessionStorage.setItem('signing_redirect_token', token || '');
    navigate('/login');
  };

  const handleSessionMismatchContinue = async () => {
    // Log out the current user, then redirect to the appropriate flow
    await logout();
    if (resolveData?.has_account) {
      setStep('has_account');
    } else {
      setStep('otp_send');
    }
  };

  const handleSigningComplete = (hasAccount: boolean) => {
    if (!hasAccount) {
      setShowPostSigningModal(true);
    } else {
      // Registered user — redirect to signing requests
      navigate('/signing-requests');
    }
  };

  // ── Render helpers ──────────────────────────────────────────

  if (step === 'loading') {
    return (
      <GateLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </GateLayout>
    );
  }

  if (step === 'error') {
    return (
      <GateLayout>
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('invalid_link')}</h3>
            <p className="text-sm text-muted-foreground">{t('invalid_link_desc')}</p>
          </CardContent>
        </Card>
      </GateLayout>
    );
  }

  if (step === 'already_actioned') {
    return (
      <GateLayout>
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('already_actioned')}</h3>
            <p className="text-sm text-muted-foreground">{t('already_actioned_desc')}</p>
          </CardContent>
        </Card>
      </GateLayout>
    );
  }

  if (step === 'session_mismatch') {
    return (
      <GateLayout>
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('session_mismatch_title')}</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('session_mismatch_desc', { email: user?.email || '' })}
            </p>
            <Button onClick={handleSessionMismatchContinue} className="w-full">
              {t('continue_button')}
            </Button>
          </CardContent>
        </Card>
      </GateLayout>
    );
  }

  if (step === 'has_account') {
    return (
      <GateLayout>
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <LogIn className="h-7 w-7 text-primary" />
            </div>
            <CardTitle>{t('has_account_title')}</CardTitle>
            <CardDescription>{t('has_account_desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleSignInRedirect} className="w-full mb-3">
              {t('sign_in_button')}
            </Button>
          </CardContent>
        </Card>
      </GateLayout>
    );
  }

  if (step === 'otp_send') {
    return (
      <GateLayout>
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <CardTitle>{t('verify_identity')}</CardTitle>
            <CardDescription>{t('verify_desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <label className="text-sm font-medium mb-1 block">{t('your_email')}</label>
              <div className="px-3 py-2 rounded-md border bg-muted/50 text-sm font-mono">
                {resolveData?.masked_email}
              </div>
            </div>

            {error && <p className="text-sm text-destructive mb-3">{error}</p>}

            <Button onClick={handleSendOtp} disabled={isSendingOtp} className="w-full mb-3">
              {isSendingOtp ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('sending_code')}</>
              ) : (
                t('send_code')
              )}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">{t('or_divider')}</span>
              </div>
            </div>

            <Button variant="ghost" onClick={handleSignInRedirect} className="w-full text-sm">
              {t('has_account_link')}
            </Button>
          </CardContent>
        </Card>
      </GateLayout>
    );
  }

  if (step === 'otp_verify') {
    return (
      <GateLayout>
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <CardTitle>{t('enter_code')}</CardTitle>
            <CardDescription>
              {t('enter_code_desc', { email: resolveData?.masked_email || '' })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && <p className="text-sm text-destructive mb-3">{error}</p>}

            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder={t('code_placeholder')}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="text-center text-2xl tracking-[0.5em] font-mono mb-4"
              autoFocus
            />

            <Button
              onClick={handleVerifyOtp}
              disabled={otpCode.length !== 6 || isVerifyingOtp}
              className="w-full mb-3"
            >
              {isVerifyingOtp ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('verifying')}</>
              ) : (
                t('verify_button')
              )}
            </Button>

            <Button variant="ghost" onClick={handleResendOtp} className="w-full text-sm">
              {t('resend_code')}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">{t('or_divider')}</span>
              </div>
            </div>

            <Button variant="ghost" onClick={handleSignInRedirect} className="w-full text-sm">
              {t('has_account_link')}
            </Button>
          </CardContent>
        </Card>
      </GateLayout>
    );
  }

  // step === 'signing'
  return (
    <>
      <SigningView
        token={token!}
        otpToken={verifiedOtpToken}
        onSigningComplete={handleSigningComplete}
      />
      <PostSigningModal
        open={showPostSigningModal}
        onOpenChange={setShowPostSigningModal}
      />
    </>
  );
}

// Minimal layout for the gate screens (no sidebar, just branding)
function GateLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('signinggate');

  return (
    <div className="min-h-screen bg-muted/20 flex flex-col">
      <header className="border-b bg-background px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/beaver.svg" alt="VivoSign" className="h-8 w-8" />
          <span className="font-bold text-lg">VivoSign</span>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-4">
        {children}
      </main>
      <footer className="border-t py-3 text-center text-xs text-muted-foreground">
        {t('powered_by')}
      </footer>
    </div>
  );
}
